import React from 'react';

import { type FieldValues, type UseFormSetValue } from 'react-hook-form';

import { Alert, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';

import type { NamedEntity, VendorOffer } from '~/types/services/parityNotifications';

interface VendorOfferSelectorProps<T extends FieldValues = FieldValues> {
  offers: VendorOffer[];
  offersFetching: boolean;
  vendorEnabled: boolean;
  selectedOfferId: string;
  setSelectedOfferId: (id: string) => void;
  selectionRatePlans?: NamedEntity[];
  selectionProperties?: NamedEntity[];
  selectionFetching: boolean;
  setValue: UseFormSetValue<T>;
  /** When true, shows the Property dropdown instead of the Rate Plan dropdown */
  showProperty?: boolean;
  disabled?: boolean;
}

export default function VendorOfferSelector<T extends FieldValues = FieldValues>({
  offers,
  offersFetching,
  vendorEnabled,
  selectedOfferId,
  setSelectedOfferId,
  selectionRatePlans = [],
  selectionProperties = [],
  selectionFetching,
  setValue,
  showProperty = false,
  disabled,
}: VendorOfferSelectorProps<T>) {
  if (!vendorEnabled) return null;

  if (offersFetching) {
    return (
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <CircularProgress size={20} />
      </Stack>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const set = setValue as UseFormSetValue<any>;

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
      {offers.length > 0 ? (
        <TextField
          select
          label="Offer"
          helperText="Sets Offer ID"
          onChange={(e) => {
            const offerId = e.target.value;
            set('offerId', offerId);
            setSelectedOfferId(offerId);
            if (showProperty) {
              set('propertyId', '');
            } else {
              set('ratePlanId', '');
            }
          }}
          sx={{ minWidth: 340 }}
          defaultValue=""
          disabled={disabled}
        >
          {offers.map((offer) => (
            <MenuItem key={offer.id_salesforce_external} value={offer.id_salesforce_external}>
              {offer.name} — {offer.id_salesforce_external}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Alert severity="info" sx={{ alignSelf: 'center' }}>
          No offers found for this vendor — enter Offer ID manually.
        </Alert>
      )}

      {selectedOfferId &&
        (selectionFetching ? (
          <CircularProgress size={20} sx={{ alignSelf: 'center' }} />
        ) : showProperty ? (
          selectionProperties.length > 0 ? (
            <TextField
              select
              label="Property"
              helperText="Sets Property ID"
              onChange={(e) => set('propertyId', e.target.value)}
              sx={{ minWidth: 300 }}
              defaultValue=""
              disabled={disabled}
            >
              {selectionProperties.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Alert severity="info" sx={{ alignSelf: 'center' }}>
              No properties found — enter Property ID manually.
            </Alert>
          )
        ) : selectionRatePlans.length > 0 ? (
          <TextField
            select
            label="Rate Plan"
            helperText="Sets Rate Plan ID"
            onChange={(e) => set('ratePlanId', e.target.value)}
            sx={{ minWidth: 300 }}
            defaultValue=""
            disabled={disabled}
          >
            {selectionRatePlans.map((rp) => (
              <MenuItem key={rp.id} value={rp.id}>
                {rp.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Alert severity="info" sx={{ alignSelf: 'center' }}>
            No rate plans found for this offer — enter Rate Plan ID manually.
          </Alert>
        ))}
    </Stack>
  );
}
